import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { AdminBillPaymentTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";

export const BILL_PAYMENT_TABLE_LIMIT = 25;

export type BillPaymentSubtypeFilterValue =
  | "airtime"
  | "data"
  | "electricity"
  | "betting";

export const BILL_PAYMENT_SUBTYPE_FILTERS: {
  value: BillPaymentSubtypeFilterValue;
  label: string;
}[] = [
  { value: "airtime", label: "Airtime" },
  { value: "data", label: "Data" },
  { value: "electricity", label: "Electricity" },
  { value: "betting", label: "Betting" },
];

export type BillPaymentTableRow = BaseRowItem & {
  raw: AdminBillPaymentTransaction;
  displayDate: string;
  userName: string;
  userEmail: string;
  subtypeLabel: string;
  detailLine: string;
  ngnLabel: string;
  usdtLabel: string;
  statusLabel: string;
  withdrawalRef: string;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function utilityBlock(
  tx: AdminBillPaymentTransaction
): Record<string, unknown> | null {
  const u = tx.utility;
  if (!u || typeof u !== "object") return null;
  return u as Record<string, unknown>;
}

function utilityData(
  tx: AdminBillPaymentTransaction
): Record<string, unknown> | null {
  const util = utilityBlock(tx);
  if (!util) return null;
  const data = util.data;
  if (data != null && typeof data === "object") {
    return data as Record<string, unknown>;
  }
  return null;
}

export function billUtilityTypeRaw(tx: AdminBillPaymentTransaction): string {
  const util = utilityBlock(tx);
  if (!util) return "";
  return str(util.type).trim();
}

export function billUtilitySubtypeLabel(tx: AdminBillPaymentTransaction): string {
  const t = billUtilityTypeRaw(tx);
  if (!t) return "—";
  const lower = t.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** `utility.type` plus parsed `utility.data` (for detail modal). */
export function getBillPaymentUtilityInfo(tx: AdminBillPaymentTransaction): {
  typeRaw: string;
  typeLabel: string;
  data: Record<string, unknown> | null;
} {
  return {
    typeRaw: billUtilityTypeRaw(tx),
    typeLabel: billUtilitySubtypeLabel(tx),
    data: utilityData(tx),
  };
}

function recipientPhone(d: Record<string, unknown>): string {
  const rec = d.receiver;
  if (rec && typeof rec === "object" && !Array.isArray(rec)) {
    const n = str((rec as { number?: unknown }).number).trim();
    if (n) return n;
  }
  return str(d.phone).trim();
}

function billUtilityDetailLine(tx: AdminBillPaymentTransaction): string {
  const d = utilityData(tx);
  if (!d) return "—";
  const parts: string[] = [];
  const phone = recipientPhone(d);
  if (phone) parts.push(phone);
  const meter = str(d.meter_number).trim();
  if (meter) parts.push(`Meter ${meter}`);
  const network = str(d.network).trim() || str(d.product).trim();
  if (network) parts.push(network);
  const service = str(d.service).trim() || str(d.serviceCategoryId).trim();
  if (service) parts.push(service);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

export function getBillPaymentDisplayDate(tx: AdminBillPaymentTransaction): string {
  const iso = str(tx.createdAt) || str(tx.updatedAt);
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function userDisplayName(tx: AdminBillPaymentTransaction): string {
  const u = tx.user;
  if (!u || typeof u !== "object") return "—";
  const profile = (u as { profile?: { firstName?: string; lastName?: string } })
    .profile;
  const f = profile?.firstName?.trim() ?? "";
  const l = profile?.lastName?.trim() ?? "";
  const name = `${f} ${l}`.trim();
  return name || "—";
}

function userEmailLine(tx: AdminBillPaymentTransaction): string {
  const u = tx.user;
  if (!u || typeof u !== "object") return "—";
  const email = (u as { email?: { address?: string } }).email?.address?.trim();
  return email || "—";
}

function usdtFromTx(tx: AdminBillPaymentTransaction): number | undefined {
  return num(tx.usdtAmount) ?? num(tx.amount);
}

export function buildBillPaymentTableRows(
  transactions: AdminBillPaymentTransaction[]
): BillPaymentTableRow[] {
  return transactions.map((tx) => {
    const usd = usdtFromTx(tx);
    const ngn = num(tx.amountNGN);
    const wid = str(tx.withdrawalId);

    return {
      id: tx._id,
      raw: tx,
      displayDate: getBillPaymentDisplayDate(tx),
      userName: userDisplayName(tx),
      userEmail: userEmailLine(tx),
      subtypeLabel: billUtilitySubtypeLabel(tx),
      detailLine: billUtilityDetailLine(tx),
      ngnLabel: ngn != null ? `₦${formatAmount(ngn)}` : "—",
      usdtLabel: usd != null ? `${formatAmount(usd)} USDT` : "—",
      statusLabel: str(tx.status) || "—",
      withdrawalRef: wid.length > 14 ? `${wid.slice(0, 10)}…${wid.slice(-4)}` : wid || "—",
    };
  });
}

export function createBillPaymentTableColumns(
  onOpen: (row: BillPaymentTableRow) => void
): ColumnDef<BillPaymentTableRow, unknown>[] {
  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "userName",
      header: "User",
      meta: { hideBelow: "sm" as const },
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className="block text-[13px] text-white">
            {row.original.userName}
          </span>
          <span className="mt-0.5 block max-w-[200px] truncate text-[11px] text-[#869AA9] sm:max-w-[240px]">
            {row.original.userEmail}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "subtypeLabel",
      header: "Subtype",
      cell: ({ getValue }) => (
        <span className="text-[12px] font-semibold uppercase tracking-wide text-[#BEE1FB]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "detailLine",
      header: "Recipient / product",
      meta: { hideBelow: "lg" as const, wrap: true as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "ngnLabel",
      header: "NGN",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] font-medium text-white">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "usdtLabel",
      header: "USDT",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] text-[#E7EBEE]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "withdrawalRef",
      header: "Withdrawal",
      meta: { hideBelow: "lg" as const },
      cell: ({ row }) => (
        <span
          className="font-mono text-[11px] text-[#869AA9]"
          title={str(row.original.raw.withdrawalId)}
        >
          {row.original.withdrawalRef}
        </span>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusBadgeClass(
            row.original.raw.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onOpen(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}
